import { createContext, useContext } from 'react';

/**
 * Marketplace cart contract — FROZEN (consumed by the product page, the cart
 * UI, and the AppMenuBar badge). The provider lives in `./CartProvider.tsx`;
 * this file follows the repo's `homepage-context.ts` split so Fast Refresh
 * sees a component-only provider file.
 */

export interface CartItem {
  /** `MarketplaceProduct.id` (== slug). */
  productId: string;
  /** Always ≥ 1. A quantity of 0 removes the line. */
  quantity: number;
}

export interface CartContextValue {
  items: CartItem[];
  /** Sum of quantities — what the toolbar badge shows. */
  count: number;
  /** Adds `quantity` (default 1) to the line, creating it if absent. */
  add: (productId: string, quantity?: number) => void;
  remove: (productId: string) => void;
  /** Sets an absolute quantity; ≤ 0 removes the line. */
  setQuantity: (productId: string, quantity: number) => void;
  clear: () => void;
  has: (productId: string) => boolean;
}

export const CartContext = createContext<CartContextValue | null>(null);

/**
 * Throws outside a provider on purpose — a silent empty cart would hide a
 * mis-mount (the provider is mounted in `App.tsx`).
 */
export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within <CartProvider>');
  return ctx;
}
